import { Box, Grid, Link, Stack, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form as FormikForm, FormikProvider, useFormik } from "formik";
import { Plus as AddIcon, Trash2, Upload } from "lucide-react";
import { useMemo } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";

import {
  getSingleCreditEvaluation,
  pushCreditEvaluationLoanApplications,
} from "api/creditEvaluations";
import { creditEvaluationsKeys } from "api/creditEvaluations/queries";
import {
  CreditEvaluationIncome,
  CreditEvaluationsType,
} from "api/creditEvaluations/types";
import { getLoanApplications } from "api/loanApplications";
import { loanApplicationsKeys } from "api/loanApplications/queries";
import { LoanApplicationsType } from "api/loanApplications/types";
import { QueryParamsType } from "api/utils";
import { ReactComponent as HubspotSVG } from "assets/icons/hubspot.svg";
import FormikAutocomplete from "components/forms/FormikAutocomplete";
import PageLayout from "components/layouts/PageLayout";
import { hasPermissions } from "components/stores/UserStore";
import { dateFormatter } from "components/Table/formatters/dateFormatter";
import UltimateButton from "components/UltimateButton";
import { useModal } from "utils/hooks/useModal";

import CreditEvaluationOverview from "./components/CreditEvaluationOverview";
import CreditScoreFormatter from "./components/CreditScore/CreditScoreFormatter";
import CreditScoreFormatterLoading from "./components/CreditScore/CreditScoreFormatterLoading";
import DebtPmtTable from "./components/DebtPtm/DebtPmtTable";
import DeleteCreditEvaluationModal from "./components/DeleteCreditEvaluationModal";
import AddIncomeModal from "./components/Income/AddIncomeModal";
import IncomesTable from "./components/Income/IncomesTable/IncomesTable";
import SummaryOfIncomesTable from "./components/Income/IncomesTable/SummaryOfIncomesTable";
import IncomeOverviewTable from "./components/IncomeOverview/IncomeOverviewTable/IncomeOveriewTable";
import LoanAffordabilities from "./components/LoanAffordabilities/LoanAffordabilities";
import LoanAffordabilitiesLoading from "./components/LoanAffordabilities/LoanAffordabilitiesLoading";
import LoanAffordabilitiesRate from "./components/LoanAffordabilities/LoanAffordabilitiesRate";
import LoanApplicationsTable from "../../loanApplications/LoanApplicationsTable/LoanApplicationsTable";
import CollectionsTable from "./components/tables/Collections/CollectionsTable";
import LoansTable from "./components/tables/Loans/LoansTable";
import PublicRecordsTable from "./components/tables/PublicRecords/PublicRecordsTable";
import TradelinesTable from "./components/tables/Tradelines/TradelinesTable";
import NewLoanApplicationModal from "../../loanApplications/LoanApplicationsTable/components/NewLoanApplicationModal";

const evaluationTitle = (c: CreditEvaluationsType | undefined) =>
  c
    ? `${c.customer?.firstName ?? ""} ${
        c.customer?.lastName ?? ""
      } – ${dateFormatter(c.reportDate)}`.trim()
    : "Credit evaluation";

const SinlgeCreditEvaluation = () => {
  const queryClient = useQueryClient();
  const { creditEvaluationId } = useParams<{ creditEvaluationId: string }>();

  const {
    data: creditEvaluation,
    status: creditEvaluationStatus,
    isLoading: isCreditEvaluationLoading,
  } = useQuery(creditEvaluationsKeys.details(creditEvaluationId), async () => {
    const { data: res } = await getSingleCreditEvaluation(creditEvaluationId);
    return res.data;
  });

  const loanApplicationsFilter: QueryParamsType<LoanApplicationsType> = {
    filter: [
      { field: "creditEvaluation", operator: "eq", value: creditEvaluationId },
    ],
    limit: 1000,
  };

  const { data: loanApplications, status: loanApplicationsStatus } = useQuery(
    loanApplicationsKeys.listFiltered(loanApplicationsFilter),
    async () => {
      const { data: res } = await getLoanApplications(loanApplicationsFilter);
      return res.data;
    },
  );

  const {
    isOpen: isOpenAddIncome,
    handleClose: handleCloseAddIncome,
    handleOpen: handleOpenAddIncome,
  } = useModal<CreditEvaluationIncome>();

  const {
    isOpen: isOpenAddLoanApplication,
    handleClose: handleCloseAddLoanApplication,
    handleOpen: handleOpenAddLoanApplication,
  } = useModal<LoanApplicationsType>();

  const {
    isOpen: isOpenDelete,
    handleClose: handleCloseDelete,
    handleOpen: handleOpenDelete,
    context: deleteContext,
  } = useModal<CreditEvaluationsType>();

  const creditScoresInquires = useMemo(() => {
    if (creditEvaluation) {
      return creditEvaluation.creditScores.map((creditScore) => {
        const matchedRecentInquires = creditEvaluation.recentInquiries.find(
          (inquire) => inquire.type === creditScore.type,
        );
        return {
          lastSixMonths: 0,
          lastTwelveMonths: 0,
          inquiries: [],
          ...matchedRecentInquires,
          ...creditScore,
        };
      });
    }
    return [];
  }, [creditEvaluation]);

  const formik = useFormik({
    initialValues: {
      tradelinesStatus: "opened",
      loansStatus: "opened",
    },
    onSubmit: () => {},
  });

  const { values } = formik;

  const tradelines = useMemo(() => {
    if (values.tradelinesStatus && values.tradelinesStatus !== "") {
      return creditEvaluation?.tradelines?.filter((tradeline) => {
        if (tradeline.accountType === "Terminated") {
          return values.tradelinesStatus !== "opened";
        }
        return (
          tradeline.status === values.tradelinesStatus ||
          (tradeline.status === "closed" && tradeline.balance > 0)
        );
      });
    }
    return creditEvaluation?.tradelines;
  }, [creditEvaluation?.tradelines, values.tradelinesStatus]);

  const loans = useMemo(() => {
    if (values.loansStatus && values.loansStatus !== "") {
      return creditEvaluation?.loans?.filter(
        (loan) =>
          loan.status === values.loansStatus ||
          (loan.status === "closed" && loan.balance > 0),
      );
    }
    return creditEvaluation?.loans;
  }, [creditEvaluation?.loans, values.loansStatus]);

  const statusOptions = [
    { label: "All", value: "" },
    { label: "Opened", value: "opened" },
    { label: "Closed", value: "closed" },
  ];

  const { mutateAsync: pushLoanApplications, isLoading: isPushLoading } =
    useMutation(async () => {
      const res =
        await pushCreditEvaluationLoanApplications(creditEvaluationId);
      await queryClient.invalidateQueries(loanApplicationsKeys.list());
      return res.data;
    });

  const hasUnpublishedLoanApplicaiton = useMemo(() => {
    if (loanApplications?.length) {
      return loanApplications.some(
        (loanApplication) => !loanApplication.upToDate,
      );
    }
    return false;
  }, [loanApplications]);

  const title = evaluationTitle(creditEvaluation);
  const subtitle =
    "Income, affordability, credit scores, and loan applications.";
  const actions = hasPermissions("update:credit-evaluations") ? (
    <UltimateButton
      text="Delete Eval"
      color="brandDanger"
      Icon={
        Trash2 as React.ComponentType<{
          size?: number;
          className?: string;
          style?: React.CSSProperties;
        }>
      }
      onClick={() => handleOpenDelete(creditEvaluation)}
    />
  ) : undefined;

  return (
    <>
      <Helmet
        title={
          creditEvaluation
            ? `Lendzee | Evaluation ${creditEvaluation.customer?.firstName} ${
                creditEvaluation.customer?.lastName
              } ${dateFormatter(creditEvaluation.reportDate)}`
            : "Lendzee | Evaluation"
        }
      />
      <PageLayout title={title} subtitle={subtitle} actions={actions}>
        <Stack
          spacing={4}
          flex={1}
          height="100%"
          overflow="scroll"
          className="scrollbar-hidden"
        >
          <CreditEvaluationOverview
            creditEvaluation={creditEvaluation}
            isLoading={isCreditEvaluationLoading}
          />

          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={4}>
                <Typography variant="headingXS" fontWeight={600} sx={{ mb: 2 }}>
                  Income
                </Typography>
                <IncomeOverviewTable
                  status={creditEvaluationStatus}
                  data={creditEvaluation?.incomesOverview || []}
                  creditEvaluationId={creditEvaluationId}
                  householdIncome={creditEvaluation?.selectedHouseholdIncome}
                />
                <DebtPmtTable
                  creditEvaluationId={creditEvaluationId}
                  initialValues={{
                    overrideDebtPayment:
                      creditEvaluation?.debtDetails.overrideDebtPayment,
                    deferredStudentLoans:
                      creditEvaluation?.debtDetails.deferredStudentLoans,
                    rentPayment: creditEvaluation?.debtDetails.rentPayment,
                    mortgagePayment:
                      creditEvaluation?.debtDetails.mortgagePayment,
                  }}
                  debtDetails={creditEvaluation?.debtDetails}
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <Typography variant="headingXS" fontWeight={600} sx={{ mb: 2 }}>
                  Loan Affordability
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <LoanAffordabilitiesRate
                    creditEvaluationId={creditEvaluationId}
                    rate={
                      !isCreditEvaluationLoading
                        ? creditEvaluation?.loanAffordabilityRate || 14
                        : undefined
                    }
                  />
                </Box>
                {isCreditEvaluationLoading ? (
                  <>
                    <LoanAffordabilitiesLoading />
                    <LoanAffordabilitiesLoading />
                  </>
                ) : (
                  <LoanAffordabilities
                    data={creditEvaluation?.loanAffordability || []}
                  />
                )}
              </Grid>
              {isCreditEvaluationLoading ? (
                <Grid item xs={12} lg={4}>
                  <CreditScoreFormatterLoading />
                </Grid>
              ) : (
                creditScoresInquires.map((creditScore) => (
                  <Grid item xs={12} lg={4} key={creditScore.type}>
                    <CreditScoreFormatter creditScore={creditScore} />
                  </Grid>
                ))
              )}
            </Grid>
          </Box>

          <FormikProvider value={formik}>
            <FormikForm>
              <Stack spacing={4}>
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={2}
                    sx={{ mb: 2 }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="headingXS" fontWeight={600}>
                        Loan Applications
                      </Typography>
                      {creditEvaluation?.hubspotDealId && (
                        <Link
                          href={`https://app.hubspot.com/contacts/44366077/record/0-3/${creditEvaluation.hubspotDealId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <HubspotSVG style={{ height: 32 }} />
                        </Link>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      {hasPermissions("update:credit-evaluations") && (
                        <UltimateButton
                          text="Push to Hubspot"
                          Icon={
                            Upload as React.ComponentType<{
                              size?: number;
                              className?: string;
                              style?: React.CSSProperties;
                            }>
                          }
                          loading={isPushLoading}
                          onClick={() => pushLoanApplications()}
                          disabled={!hasUnpublishedLoanApplicaiton}
                        />
                      )}
                      {hasPermissions("update:credit-evaluations") && (
                        <UltimateButton
                          text="Add Loan Application"
                          Icon={
                            AddIcon as React.ComponentType<{
                              size?: number;
                              className?: string;
                              style?: React.CSSProperties;
                            }>
                          }
                          onClick={() => handleOpenAddLoanApplication()}
                        />
                      )}
                    </Stack>
                  </Stack>
                  <LoanApplicationsTable
                    data={loanApplications || []}
                    status={loanApplicationsStatus}
                  />
                </Box>

                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="headingXS" fontWeight={600}>
                      Tradelines
                    </Typography>
                    <FormikAutocomplete
                      name="tradelinesStatus"
                      label="Status"
                      options={statusOptions}
                      sx={{ width: 200 }}
                    />
                  </Stack>
                  <TradelinesTable
                    data={tradelines || []}
                    status={creditEvaluationStatus}
                  />
                </Box>

                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="headingXS" fontWeight={600}>
                      Loans
                    </Typography>
                    <FormikAutocomplete
                      name="loansStatus"
                      label="Status"
                      options={statusOptions}
                      sx={{ width: 200 }}
                    />
                  </Stack>
                  <LoansTable
                    data={loans || []}
                    status={creditEvaluationStatus}
                  />
                </Box>
              </Stack>
            </FormikForm>
          </FormikProvider>

          <Box>
            <Typography variant="headingXS" fontWeight={600} sx={{ mb: 2 }}>
              Collections
            </Typography>
            <CollectionsTable
              data={creditEvaluation?.collections || []}
              status={creditEvaluationStatus}
            />
          </Box>

          <Box>
            <Typography variant="headingXS" fontWeight={600} sx={{ mb: 2 }}>
              Public Records
            </Typography>
            <PublicRecordsTable
              data={creditEvaluation?.publicRecords || []}
              status={creditEvaluationStatus}
            />
          </Box>

          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="headingXS" fontWeight={600}>
                Income Sources
              </Typography>
              {hasPermissions("update:credit-evaluations") && (
                <UltimateButton
                  text="Add Income"
                  Icon={
                    AddIcon as React.ComponentType<{
                      size?: number;
                      className?: string;
                      style?: React.CSSProperties;
                    }>
                  }
                  onClick={() => handleOpenAddIncome()}
                />
              )}
            </Stack>
            <IncomesTable
              creditEvaluationId={creditEvaluationId}
              data={creditEvaluation?.incomes || []}
              status={creditEvaluationStatus}
            />
          </Box>

          <Box>
            <Typography variant="headingXS" fontWeight={600} sx={{ mb: 2 }}>
              Summary of Income Calculations
            </Typography>
            <SummaryOfIncomesTable
              data={creditEvaluation?.summaryOfIncomes}
              status={creditEvaluationStatus}
              creditEvaluationId={creditEvaluationId}
            />
          </Box>
        </Stack>
      </PageLayout>

      {hasPermissions("update:credit-evaluations") && (
        <>
          <AddIncomeModal
            creditEvaluationId={creditEvaluationId}
            isOpen={isOpenAddIncome}
            handleClose={handleCloseAddIncome}
          />
          <NewLoanApplicationModal
            isOpen={isOpenAddLoanApplication}
            handleClose={handleCloseAddLoanApplication}
            creditEvaluationId={creditEvaluationId}
          />
          <DeleteCreditEvaluationModal
            isOpen={isOpenDelete}
            handleClose={handleCloseDelete}
            creditEvaluation={deleteContext}
          />
        </>
      )}
    </>
  );
};

export default SinlgeCreditEvaluation;
